import { cineamoApiClient } from '../../apiClient';
import { RequestConfig } from '../../fetch-helper';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { ContentDto } from '../../models/content/ContentDto.types';
import { MovieTmdbImagesDto } from '../../models/movie/MovieTmdbImagesDto.types';
import { MovieDto } from '../../models/movie/v1/MovieDto.types';
import { MovieAgeRatingDto, MovieAgeRatingInputDto } from '../../models/movie-age-rating/MovieAgeRatingDto.types';
import { TrailerCategory } from '../../models/movie-trailer/TrailerCategory.enum';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import {
    MovieListResponse,
    MovieAgeRatingListResponse,
    MovieTrailerListResponse
} from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';
import { CountryCode } from '../../types/CountryCode';

// <------------------------ Movies ------------------------>

type GetMoviesQueryParameter = UrlQueryParameter & {
    releaseDateStart?: string;
    releaseDateEnd?: string;
    region?: string;
    distributorId?: string;
    isShowingNow?: boolean;
    cinemaCountryCode?: CountryCode;
};

/**
 * This endpoint returns a list of movies.
 *
 * @deprecated Use `getMovies` from `@cineamo/api/resources/movie/movie-v2-api` instead.
 *             The V2 endpoint is locale-aware and cinema-customizable.
 *
 * @example
 * // Get a list of movies:
 * getMovies({...urlParams})
 *
 * @param urlParams
 * @returns {ApiResponse<MovieListResponse>}
 */

export async function getMovies(urlParams?: GetMoviesQueryParameter): Promise<ApiResponse<MovieListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<MovieListResponse>>(`/movies${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Movie ------------------------>

/**
 * This endpoint returns a movie.
 *
 * @deprecated Use `getMovieById` from `@cineamo/api/resources/movie/movie-v2-api` instead.
 *             The V2 endpoint is locale-aware and cinema-customizable.
 *
 * @example
 * // Get a movie by id:
 * getMovies(1)
 *
 * @param movieId
 * @param config
 * @returns {ApiResponse<MovieDto>}
 */

export async function getMovieById(
    // Still accepts the deprecated cineamoId (`…m`) because it is the only way to address a movie
    // by its TMDB id — the API exposes no numeric tmdbId lookup or filter (CIN-13072).
    movieId: MovieDto['id'] | MovieDto['cineamoId'],
    config?: RequestConfig
): Promise<ApiResponse<MovieDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<MovieDto>>(`/movies/${movieId}`, {
            ...(config ?? {}),
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

export async function getMovieContent(
    movieId: MovieDto['id'],
    config?: RequestConfig
): Promise<ApiResponse<ContentDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<ContentDto>>(`/movies/${movieId}/content`, {
            ...(config ?? {}),
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

/**
 * Returns the complete TMDB artwork inventory of a movie (admin only, CIN-14129).
 * Unlike the public movie endpoints, globally disabled images are included here
 * (flagged via isDisabled) so an admin can re-enable them.
 *
 * `appLocaleKeys` names the locales to report `servedForLocaleKeys` and
 * `candidateForLocaleKeys` for (CIN-14885). Which locales an app serves is the app's
 * own fact, so it is sent rather than mirrored server-side; omit it and both arrays
 * come back empty.
 *
 * @param movieId
 * @param appLocaleKeys
 */
export async function getMovieTmdbImages(
    movieId: MovieDto['id'],
    appLocaleKeys: readonly string[] = []
): Promise<MovieTmdbImagesDto | null> {
    const query = appLocaleKeys.length > 0 ? `?localeKeys=${encodeURIComponent(appLocaleKeys.join(','))}` : '';
    return cineamoApiClient
        .get<MovieTmdbImagesDto>(`/movies/${movieId}/tmdb-images${query}`, {
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

/**
 * This endpoint syncs Movie with TMDB.
 *
 * Keyed on the **TMDB id**, not the internal `Movie.id`: the handler resolves the movie via
 * `cineamoId = "{id}m"`, so the path segment is a tmdbId despite the route naming it `movieId`.
 *
 * @param tmdbId
 */
export async function fetchTmdbMovie(tmdbId: MovieDto['tmdbId']): Promise<boolean> {
    return cineamoApiClient
        .post(`/movies/${tmdbId}/fetch-tmdb`)
        .then((response) => response.status === 204)
        .catch(() => false);
}

// <------------------------ Movie Age Ratings ------------------------>

type GetMovieAgeRatingsQueryParameter = UrlQueryParameter & {
    ageRatingIds?: number[];
    iso31662s?: string[];
};

export function getMovieAgeRatings(
    movieId: MovieDto['id'],
    params?: GetMovieAgeRatingsQueryParameter
): Promise<ApiResponse<MovieAgeRatingListResponse> | null> {
    if (!movieId) {
        return Promise.resolve(null);
    }
    return cineamoApiClient
        .get<ApiResponse<MovieAgeRatingListResponse>>(`/movies/${movieId}/age-ratings` + buildUrlQuery(params), {
            credentials: 'same-origin'
        })
        .then((res) => res.data)
        .catch(() => {
            return null;
        });
}

export function setMovieAgeRating(
    movieId: MovieDto['id'],
    movieAgeRating: MovieAgeRatingInputDto
): Promise<ApiResponse<MovieAgeRatingDto> | ApiErrorResponse<MovieAgeRatingInputDto>> {
    return cineamoApiClient
        .put(`/movies/${movieId}/age-ratings`, movieAgeRating)
        .then((res) => res.data)
        .catch((error) => error);
}
// <------------------------ Movie Trailers ------------------------>

type GetMovieTrailersQueryParameter = UrlQueryParameter & {
    movieId?: number;
    categories?: TrailerCategory[];
};

/**
 * This endpoint returns a list of movie trailers (from KinoCheck).
 *
 * @example
 * // Get the movie trailers for a specific movie:
 * getMovieTrailers(123);
 *
 * @param movieId
 * @param params
 * @returns {ApiResponse<MovieTrailerListResponse>}
 */
export function getMovieTrailers(
    movieId: number,
    params?: GetMovieTrailersQueryParameter
): Promise<ApiResponse<MovieTrailerListResponse> | null> {
    if (!movieId) {
        return Promise.resolve(null);
    }
    return cineamoApiClient
        .get<ApiResponse<MovieTrailerListResponse>>(`/movies/${movieId}/trailers` + buildUrlQuery(params))
        .then((res) => res.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Delete Movie ------------------------>

/**
 * Soft-delete a movie.
 *
 * @param movieId - The internal id of the movie to delete
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export async function deleteMovie(movieId: MovieDto['id']): Promise<boolean> {
    return cineamoApiClient
        .delete(`/movies/${movieId}`)
        .then((response) => response.status === 204)
        .catch(() => false);
}

// <------------------------ Patch Movie ------------------------>

export type MovieDtoInput = {
    isTmdbSyncSkipped?: boolean;
    rentrakTitle?: number | null;
    overridePosterImageRef?: string | null;
    overrideBackdropImageRef?: string | null;
    overrideLogoImageRef?: string | null;
    isPosterHidden?: boolean;
    isBackdropHidden?: boolean;
    isLogoHidden?: boolean;
    /**
     * Admin-chosen primary poster per locale key ('de', 'de-CH', 'xx' = language-less posters),
     * values are TMDB file paths or 'upload:<uuid>' refs; merge-patch semantics: sent keys are
     * merged, a null value removes that locale's choice, top-level null clears the whole map
     */
    primaryPosterPaths?: Record<string, string | null> | null;
    /**
     * Admin-chosen primary backdrop per locale key, same semantics as primaryPosterPaths;
     * the 'xx' entry effectively applies to every language (language-less backdrops top
     * the serving ladder)
     */
    primaryBackdropPaths?: Record<string, string | null> | null;
    /** Admin-chosen primary logo per locale key, same semantics as primaryPosterPaths */
    primaryLogoPaths?: Record<string, string | null> | null;
};

/** One of a movie's custom image uploads (CIN-14535). */
export type MovieImageUploadDto = {
    /**
     * The upload's identity, everywhere. Delete it by this, and write
     * `upload:<assetRef>` into a primary map to make it a locale's pick — which is what
     * `primaryValue` already spells out.
     */
    assetRef: string;
    type: 'poster' | 'backdrop' | 'logo';
    primaryValue: string;
    assetUrl: string;
    /** Null for uploads backfilled from the superseded single-ref columns. */
    createdAt: string | null;
    /**
     * The locale keys whose primary pick names this upload — the locales it is actually served to.
     * Empty means it is stored and shown to nobody, which is the state most worth surfacing: an
     * upload does nothing until it is picked. Reported here because the picks live in the movie's
     * per-locale maps, which the movie endpoints do not publish.
     */
    primaryForLocaleKeys: string[];
};

/**
 * Every custom image uploaded for this movie, across all three types (CIN-14535).
 *
 * The admin gallery does not need this — the TMDB image inventory already reports each
 * upload as an entry with `isCustomUpload` — but the upload modal does, to list what the
 * movie already holds before anything is added.
 */
export async function getMovieImageUploads(movieId: MovieDto['id']): Promise<MovieImageUploadDto[] | null> {
    return cineamoApiClient
        .get<MovieImageUploadDto[]>(`/movies/${movieId}/image-uploads`, { credentials: 'same-origin' })
        .then((response) => response.data)
        .catch(() => null);
}

/**
 * Attaches an already-uploaded asset to the movie as a custom image of one type.
 *
 * Adding an upload shows it to nobody by itself: it becomes visible to viewers only once
 * a locale's primary pick names it, which is a separate PATCH on the movie.
 */
export async function createMovieImageUpload(
    movieId: MovieDto['id'],
    type: MovieImageUploadDto['type'],
    assetRef: string
): Promise<MovieImageUploadDto | null> {
    return cineamoApiClient
        .post<MovieImageUploadDto>(`/movies/${movieId}/image-uploads`, { type, assetRef })
        .then((response) => response.data)
        .catch(() => null);
}

/**
 * Deletes ONE upload. The movie's other uploads survive, and so do their picks — only the
 * locales that pointed at this one are cleared, and those fall back to the automatic pick.
 */
export async function deleteMovieImageUpload(movieId: MovieDto['id'], assetRef: string): Promise<boolean> {
    return cineamoApiClient
        .delete(`/movies/${movieId}/image-uploads/${assetRef}`, { credentials: 'same-origin' })
        .then(() => true)
        .catch(() => false);
}

export async function updateMovie(movieId: MovieDto['id'], data: MovieDtoInput): Promise<ApiResponse<MovieDto> | null> {
    return cineamoApiClient
        .patch<ApiResponse<MovieDto>>(`/movies/${movieId}`, data)
        .then((response) => response.data)
        .catch(() => null);
}
